<template>
  <div class="relative" ref="dropdownRef">
    <button
      @click.stop="toggleDropdown"
      :class="triggerClasses"
      aria-label="Change language"
    >
      <GlobeIcon class="w-5 h-5 flex-none" />
      <span v-if="variant === 'compact'" class="text-sm font-medium uppercase">
        {{ currentLocale }}
      </span>
      <span v-else class="text-sm font-medium">
        {{ currentLanguageName }}
      </span>
      <ChevronDownWhite
        class="w-4 h-4 transition-transform"
        :class="[
          { 'rotate-180': isOpen },
          variant === 'full' ? 'ml-auto' : '',
        ]"
      />
    </button>

    <div v-if="isOpen" :class="menuClasses">
      <button
        v-for="lang in languages"
        :key="lang.locale"
        @click="switchLanguage(lang.locale)"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors',
          lang.locale === currentLocale
            ? 'text-brand-600 font-semibold bg-gray-50'
            : 'text-gray-700',
        ]"
      >
        {{ lang.name }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import GlobeIcon from "../icons/GlobeIcon.vue";
import ChevronDownWhite from "../icons/ChevronDownWhite.vue";

const props = defineProps({
  languages: {
    type: Array,
    required: true,
  },
  currentLocale: {
    type: String,
    required: true,
  },
  variant: {
    type: String,
    default: "compact",
    validator: (v) => ["compact", "full"].includes(v),
  },
});

const isOpen = ref(false);
const dropdownRef = ref(null);

const locale = window.location.pathname.split("/")[1] || "en";

const currentLanguageName = computed(() => {
  const lang = props.languages.find((l) => l.locale === props.currentLocale);
  return lang ? lang.name : props.currentLocale;
});

const triggerClasses = computed(() => {
  if (props.variant === "compact") {
    return "flex items-center gap-1 text-white p-2 hover:bg-brand-700 rounded-md transition-colors";
  }
  return "flex items-center gap-2 w-full text-white py-2 px-3 rounded-lg hover:bg-brand-700 transition-colors";
});

const menuClasses = computed(() => {
  if (props.variant === "compact") {
    return "absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg py-1 min-w-[140px] z-50";
  }
  return "absolute bottom-full left-0 right-0 mb-1 bg-white rounded-lg shadow-lg py-1 z-50";
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const switchLanguage = (newLocale) => {
  if (newLocale === props.currentLocale) {
    isOpen.value = false;
    return;
  }
  const path = window.location.pathname;
  const newPath = path.replace(`/${locale}/`, `/${newLocale}/`);
  window.location.href = newPath + window.location.search;
};

const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>
